import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { FIELD_CLASS, HELP_CLASS } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import {
  buildMonthOptions,
  counterLevel,
  usagePercent,
} from "../whatsapp/whatsappCounterRules";

const NUMBER_FORMAT = new Intl.NumberFormat("es-MX");

/**
 * Almikia Epic EN (TASK-091): the usage counter of the ALERTS number ('Numero de avisos'):
 * service messages used this month out of the free quota, a progress bar, an 80 % and a 100 %
 * warning pill and the count of paid templates. Rendered inside the direccion-only screen; the
 * `internal_notice_usage` RPC rejects everyone else anyway. One aggregate call per month; no phone
 * or phone number id exists in the figures. `now` is injectable for tests.
 */
export const InternalNoticeUsageCard = ({ now }: { now?: Date }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [monthKey, setMonthKey] = useState<string | null>(null);
  const options = useMemo(() => buildMonthOptions(now ?? new Date()), [now]);
  const selected =
    options.find((option) => option.key === monthKey) ?? options[0];
  const usage = useQuery({
    queryKey: ["internalNotices", "usage", selected.anchor],
    queryFn: () => dataProvider.getInternalNoticeUsage(selected.anchor),
    retry: false,
  });
  const data = usage.data;
  const level = data
    ? counterLevel(data.serviceMessages, data.monthlyFreeQuota)
    : "ok";

  return (
    <Card data-testid="internal-notice-usage-card">
      <CardContent className="space-y-3 p-4">
        <h2 className="text-lg font-semibold">
          {translate("crm.internalNotices.usage.title")}
        </h2>
        <div className="space-y-1">
          <Label htmlFor="internal-notice-usage-month">
            {translate("crm.internalNotices.usage.month_label")}
          </Label>
          <select
            id="internal-notice-usage-month"
            className={FIELD_CLASS}
            value={selected.key}
            onChange={(event) => setMonthKey(event.target.value)}
          >
            {options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {usage.error ? (
          <ListErrorState
            message={translate("crm.internalNotices.usage.error")}
            onRetry={() => usage.refetch()}
          />
        ) : !data ? (
          <ListCardsSkeleton rows={1} />
        ) : (
          <div className="space-y-2">
            <p className="text-base tabular-nums">
              {translate("crm.internalNotices.usage.used", {
                used: NUMBER_FORMAT.format(data.serviceMessages),
                quota: NUMBER_FORMAT.format(data.monthlyFreeQuota),
              })}
            </p>
            <Progress
              aria-label={translate("crm.internalNotices.usage.progress_label")}
              value={usagePercent(data.serviceMessages, data.monthlyFreeQuota)}
            />
            {level !== "ok" ? (
              <div role="status">
                <StatusPill
                  tone="not-ok"
                  text={translate(
                    level === "full"
                      ? "crm.internalNotices.usage.warning_100"
                      : "crm.internalNotices.usage.warning_80",
                  )}
                />
              </div>
            ) : null}
            <p className="text-base tabular-nums">
              {translate("crm.internalNotices.usage.paid_templates", {
                count: NUMBER_FORMAT.format(data.paidTemplates),
              })}
            </p>
          </div>
        )}
        <p className={HELP_CLASS}>
          {translate("crm.internalNotices.usage.note")}
        </p>
      </CardContent>
    </Card>
  );
};
